import { CanceledError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAlertView } from '~/app';
import { getActivationPreview } from '~/features/employer/billing/api';
import type { ActivationPreview, FeeAuthorization } from '~/features/employer/billing/types';
import { useManagePaymentMethod } from '~/features/employer/billing/useManagePaymentMethod';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { MARKET_READ_TIMEOUT_MS } from '../requestPolicy';
import type { ServiceOpportunity } from './schemas';

type Params = {
  responseId: number;
  viewerRole: ServiceOpportunity['viewerRole'] | undefined;
  /** The offer currently on screen; a dialog opened for another offer is dropped. */
  currentOfferId: number | null;
  /** [MCV-1933] Carries the fee authorization when the dialog showed a fee schedule. */
  onAccept: (expectedNegotiationId: number, feeAuthorization?: FeeAuthorization) => void;
};

/**
 * [MCV-1858] The hiring employer of a SERVICE response pays the commitment, so their Accept
 * previews it and shows the shared ActivationFeeDialog first (mirroring B2BOpportunitiesWorkspace
 * and NegotiationDetails). The provider, who owns the listing and is never billed, accepts
 * straight away.
 */
export const useServiceAcceptFee = ({
  responseId,
  viewerRole,
  currentOfferId,
  onAccept,
}: Params) => {
  const alertView = useAlertView();
  const alertViewRef = useRef(alertView);
  alertViewRef.current = alertView;
  const handleManagePaymentMethod = useManagePaymentMethod();
  const [dialog, setDialog] = useState<{
    expectedNegotiationId: number;
    preview: ActivationPreview;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);
  const currentOfferIdRef = useRef(currentOfferId);
  currentOfferIdRef.current = currentOfferId;
  const onAcceptRef = useRef(onAccept);
  onAcceptRef.current = onAccept;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // A dialog opened for an offer that has since been replaced would confirm the wrong terms.
  useEffect(() => {
    if (dialog && dialog.expectedNegotiationId !== currentOfferId) setDialog(null);
  }, [dialog, currentOfferId]);

  const requestAccept = useCallback(
    async (expectedNegotiationId: number) => {
      if (viewerRole !== 'RESPONDER') {
        onAcceptRef.current(expectedNegotiationId);
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setPreviewing(true);
      let preview: ActivationPreview;
      try {
        preview = await withRequestDeadline(
          (signal) => getActivationPreview(responseId, signal),
          MARKET_READ_TIMEOUT_MS,
          controller.signal
        );
      } catch (err) {
        if (!mountedRef.current) return;
        if (abortRef.current === controller) setPreviewing(false);
        // Superseded by a newer Accept or an unmount: nothing to report.
        if (err instanceof CanceledError) return;
        console.error('Failed to preview activation billing', err);
        // Nothing may be accepted without the employer having seen the fee.
        alertViewRef.current.send({
          type: 'ON_SHOW_ALERT',
          payload: {
            text: 'Could not preview the commitment',
            subText: 'Please try accepting the offer again.',
            type: 'error',
            timeout: 5000,
          },
        });
        return;
      }
      if (!mountedRef.current || abortRef.current !== controller) return;
      setPreviewing(false);
      // The offer moved while the preview was in flight: the employer has not seen this one.
      if (currentOfferIdRef.current !== expectedNegotiationId) return;
      if (preview.isBillable && preview.isCurrentUserPayer) {
        setDialog({ expectedNegotiationId, preview });
        return;
      }
      onAcceptRef.current(expectedNegotiationId);
    },
    [responseId, viewerRole]
  );

  const close = useCallback(() => setDialog(null), []);
  const confirm = useCallback(
    (feeAuthorization?: FeeAuthorization) => {
      if (!dialog) return;
      const { expectedNegotiationId } = dialog;
      setDialog(null);
      onAcceptRef.current(expectedNegotiationId, feeAuthorization);
    },
    [dialog]
  );

  return {
    requestAccept,
    previewing,
    dialogProps: {
      open: dialog != null,
      preview: dialog?.preview ?? null,
      onClose: close,
      onConfirm: confirm,
      onManagePaymentMethod: handleManagePaymentMethod,
    },
  };
};
